import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import Header from "@/components/Header";
import CrosswordGrid from "@/components/CrosswordGrid";
import CrosswordClues from "@/components/CrosswordClues";
import CompletionDialog from "@/components/CompletionDialog";
import Timer from "@/components/Timer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CrosswordCell, Clue } from "@/utils/crosswordGenerator";
import { toast } from 'sonner';
import { 
  LocationState, 
  crosswordUiCell, 
  UiClue, 
  crossWordGridToUiGrid, 
  crossWordCluesToUiClues, 
  shiftCluesDown 
} from '@/utils/crosswordUtils';
import { useIsMobile } from "@/hooks/use-mobile";
import MobileCrosswordLayout from "@/components/MobileCrosswordLayout";

const CrosswordPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState;
  const isMobile = useIsMobile();
  
  const [gridData, setGridData] = useState<crosswordUiCell[][]>([]);
  const [acrossClues, setAcrossClues] = useState<UiClue[]>([]);
  const [downClues, setDownClues] = useState<UiClue[]>([]);
  const [direction, setDirection] = useState<'across' | 'down'>('across');
  const [activeCell, setActiveCell] = useState<{ row: number; col: number } | null>(null);
  const [activeClueNumber, setActiveClueNumber] = useState<number | null>(null);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [completionTime, setCompletionTime] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [shareUrl, setShareUrl] = useState<string>('');
  const [currentClue, setCurrentClue] = useState<UiClue | null>(null);
  const lastCheckRef = useRef<number>(0);

  useEffect(() => {
    if (!state) {
      navigate('/');
      return;
    }

    if (!state.grid || !state.acrossClues || !state.downClues) {
      navigate('/create');
      return;
    }

    // Set share URL if UUID is available in state
    if (state.uuid) {
      setShareUrl(`${window.location.origin}/share/${state.uuid}`);
    }

    const newGridData = crossWordGridToUiGrid(state.grid);
    const newAcrossClues = crossWordCluesToUiClues(state.acrossClues, newGridData, 'across');
    const newDownClues = crossWordCluesToUiClues(state.downClues, newGridData, 'down');
    shiftCluesDown(newGridData, newAcrossClues, newDownClues);
    setGridData(newGridData);
    setAcrossClues(newAcrossClues);
    setDownClues(newDownClues);

    // Set initial active cell
    if (newAcrossClues.length > 0) {
      const firstClue = newAcrossClues[0];
      setActiveCell({ row: firstClue.row, col: firstClue.col });
      setActiveClueNumber(firstClue.number);
      setCurrentClue(firstClue);
    }
    else {
      const firstClue = newDownClues[0];
      setActiveCell({ row: firstClue.row, col: firstClue.col });
      setActiveClueNumber(firstClue.number);
      setCurrentClue(firstClue);
    }

    
    // Start the timer
    setIsTimerRunning(true);
    
    // Toast notification
    // toast.success(`Your crossword is ready!`);
  }, [state, navigate]);

  // Update current clue whenever active clue number or direction changes
  useEffect(() => {
    if (activeClueNumber) {
      const clues = direction === 'across' ? acrossClues : downClues;
      const activeClue = clues.find(clue => clue.number === activeClueNumber);
      if (activeClue) {
        setCurrentClue(activeClue);
      }
    }
  }, [activeClueNumber, direction, acrossClues, downClues]);

  const handleCellChange = (row: number, col: number, value: string) => {
    const newGrid = [...gridData];
    if(!newGrid[row][col].isRevealed) {
      const curVal = newGrid[row][col].currValue;
      if(curVal !== value) {
        newGrid[row][col] = { ...newGrid[row][col], currValue: value, isIncorrect: false };
        setGridData(newGrid);
        
        // Ensure timer keeps running during cell input
        if (!isTimerRunning && !isComplete) {
          setIsTimerRunning(true);
        }
        
        // Check if puzzle is complete after a delay
        setTimeout(() => {
          checkCompletion(newGrid);
        }, 500);
      }
    }
  };

  const handleClueSelect = (row: number, col: number, newDirection: 'across' | 'down') => {
    const cell = gridData[row][col];
    if (cell && cell.number) {
      setActiveCell({ row, col });
      setDirection(newDirection);
      setActiveClueNumber(cell.number);
    }
  };

  const checkCompletion = (grid: crosswordUiCell[][]) => {
    const now = Date.now();
    if (now - lastCheckRef.current < 500) {
      return;
    }
    lastCheckRef.current = now;

    // Get all cells that should have values (cells with answers)
    const cellsWithAnswers = grid.flat().filter(cell => cell.answer != '');
    
    // Check if all cells that should have values have matching currValue and answer
    const isCompleted = cellsWithAnswers.every(cell => cell.currValue === cell.answer);
    
    if (isCompleted && !isComplete) {
      setIsComplete(true);
      setIsTimerRunning(false);
      setShowCompletionDialog(true);
    }

    const isIncorrect = cellsWithAnswers.every(cell => cell.currValue != '');
    if(isIncorrect && !isComplete && !isCompleted) {
      toast.error("Crossword is incorrect!");
    }
  };

  const handleResetTimer = () => {
    // Reset the grid
    setGridData(prevGrid => {
      const newGrid = [...prevGrid];
      for (let row = 0; row < newGrid.length; row++) {
        for (let col = 0; col < newGrid[row].length; col++) {
          if (newGrid[row][col].currValue != '') {
            newGrid[row][col] = { ...newGrid[row][col], currValue: '' };
          }
        }
      }
      return newGrid;
    });
    
    // Reset completion state
    setIsComplete(false);
    setIsTimerRunning(true);
  };

  const handleTimerStop = (time: number) => {
    setCompletionTime(time);
  };

  const calculateGridSize = () => {
    if (!gridData.length || !gridData[0].length) return 0;
    
    // Calculate the maximum dimension
    const maxDimension = Math.max(gridData.length, gridData[0].length);
    
    // Calculate cell size based on viewport width
    const viewportWidth = window.innerWidth;
    const maxGridWidth = viewportWidth * (isMobile ? 0.95 : 0.8); // 95% for mobile, 80% for desktop
    const cellSize = Math.min(36, Math.floor(maxGridWidth / maxDimension));
    
    return cellSize;
  };

  const handleCheck = () => {
    const gridClone = [...gridData];
    let allCorrect = true;
    
    for (let row = 0; row < gridClone.length; row++) {
      for (let col = 0; col < gridClone[row].length; col++) {
        const cell = gridClone[row][col];
        if (cell.answer !== '' && cell.currValue !== '') {
          if (cell.currValue !== cell.answer) {
            allCorrect = false;
            gridClone[row][col] = { ...gridClone[row][col], isIncorrect: true };
          }
        }
      }
    }

    setGridData(gridClone);

    if (allCorrect) {
      toast.success("All filled squares are correct!");
    }

    
  };

  const handleReveal = (type: 'square' | 'word' | 'puzzle') => {
    const newGrid = [...gridData];
    
    if (type === 'square' && activeCell) {
      const { row, col } = activeCell;
      newGrid[row][col] = { ...newGrid[row][col], currValue: newGrid[row][col].answer, isRevealed: true, isIncorrect: false };
    } else if (type === 'word' && activeCell) {
      let { row, col } = activeCell;
      let activeRow = row;
      let activeCol = col;
      if(direction === 'across') {
        while(col < newGrid[0].length && newGrid[row][col].answer != '') {
          newGrid[row][col] = { ...newGrid[row][col], currValue: newGrid[row][col].answer, isRevealed: true, isIncorrect: false };
          col++;
        }
        col = activeCol - 1;
        while(col >= 0 && newGrid[row][col].answer != '') {
          newGrid[row][col] = { ...newGrid[row][col], currValue: newGrid[row][col].answer, isRevealed: true, isIncorrect: false };
          col--;
        }
      }
      else {
        while(row < newGrid.length && newGrid[row][col].answer != '') {
          newGrid[row][col] = { ...newGrid[row][col], currValue: newGrid[row][col].answer, isRevealed: true, isIncorrect: false };
          row++;
        }
        row = activeRow - 1;
        while(row >= 0 && newGrid[row][col].answer != '') {
          newGrid[row][col] = { ...newGrid[row][col], currValue: newGrid[row][col].answer, isRevealed: true, isIncorrect: false };
          row--;
        }
      }
    } else if (type === 'puzzle') {
      for (let row = 0; row < newGrid.length; row++) {
        for (let col = 0; col < newGrid[row].length; col++) {
          if (newGrid[row][col].answer !== '') {
            newGrid[row][col] = { ...newGrid[row][col], currValue: newGrid[row][col].answer, isRevealed: true, isIncorrect: false };
          }
        }
      }
    }
    
    setGridData(newGrid);
    
    // Check if puzzle is complete after revealing
    setTimeout(() => {
      checkCompletion(newGrid);
    }, 500);
  };  

  const handleShare = async () => {
    try {
      if (!shareUrl) {
        console.error('Share URL is empty');
        return;
      }
      await navigator.clipboard.writeText(shareUrl);
    } catch (err) {
      console.error('Error copying to clipboard:', err);
      toast.error('Failed to copy URL to clipboard');
    }
  };

  if (isMobile) {
    return (
      <MobileCrosswordLayout
        title={state?.title || 'Crossword'}
        gridData={gridData}
        onCellChange={handleCellChange}
        activeCell={activeCell}
        setActiveCell={setActiveCell}
        setActiveClue={setActiveClueNumber}
        direction={direction}
        setDirection={setDirection}
        isComplete={isComplete}
        isTimerRunning={isTimerRunning}
        onReset={handleResetTimer}
        onTimerStop={handleTimerStop}
        onCheck={handleCheck}
        onReveal={handleReveal}
        onShare={handleShare}
        currentClue={currentClue}
        acrossClues={acrossClues}
        downClues={downClues}
        activeClueNumber={activeClueNumber}
        onClueSelect={handleClueSelect}
        showCompletionDialog={showCompletionDialog}
        setShowCompletionDialog={setShowCompletionDialog}
        completionTime={completionTime}
        shareUrl={shareUrl}
      />
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-5xl font-extrabold">
            {state?.title || 'Crossword'}
          </h1>
        </div>
        
        <div className="grid gap-8 lg:grid-cols-7">
          <div className="lg:col-span-4 flex flex-col gap-4">
            <Card>
              <CardContent className="p-4">
                <Timer 
                  isRunning={isTimerRunning} 
                  isComplete={isComplete}
                  onReset={handleResetTimer}
                  onTimerStop={handleTimerStop}
                  onCheck={handleCheck}
                  onReveal={handleReveal}
                  onShare={handleShare}
                  interestTopic={state?.title || 'Crossword'}
                  shareUrl={shareUrl}
                />
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="flex justify-center p-6">
                {gridData.length > 0 && (
                  <div style={{ 
                    width: '100%', 
                    maxWidth: `${calculateGridSize() * Math.max(gridData.length, gridData[0].length)}px`,
                    overflow: 'auto'
                  }}>
                    <CrosswordGrid
                      gridData={gridData}
                      onCellChange={handleCellChange}
                      activeCell={activeCell}
                      setActiveCell={setActiveCell}
                      setActiveClue={setActiveClueNumber}
                      direction={direction}
                      setDirection={setDirection}
                      isComplete={isComplete}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
            
            {isComplete && (
              <Button variant="outline" asChild>
                <Link to="/create">
                  Create Another Crossword
                </Link>
              </Button>
            )}
          </div>
          
          <div className="lg:col-span-3">
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-bold mb-4">Clues</h2>
                {acrossClues.length > 0 && (
                  <CrosswordClues
                    acrossClues={acrossClues}
                    downClues={downClues}
                    activeClueNumber={activeClueNumber}
                    direction={direction}
                    onClueSelect={handleClueSelect}
                  />
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      <CompletionDialog
        isOpen={showCompletionDialog}
        onClose={() => setShowCompletionDialog(false)}
        completionTime={completionTime}
        interestTopic={state?.title || 'Crossword'}
        shareUrl={shareUrl}
        isMobile={isMobile}
      />
      
      <footer className="mt-auto py-2 sm:py-3 bg-secondary/80" style={{ color: '#283618' }}>
        <div className="container mx-auto px-4 text-center text-sm text-primary">
          <p>© 2025 Crossable. All rights reserved.</p>
          <p className="mt-2">Business inquiries, feedback, or bug reports? Reach us at crossabledev@gmail.com</p>
        </div>
      </footer>
    </div>
  );
};

export default CrosswordPage;
